import React, { useState, useEffect } from 'react';
import Header from "../Header/Header";
import LoadingOverlay from "../LoadingOverlay";
import '../../Styles/Mailing/MailTracking.scss';

const MailTracking = () => {
  const [allMails, setAllMails] = useState([]);
  const [filteredMails, setFilteredMails] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [paginatedMails, setPaginatedMails] = useState([]);

  const [selectedMail, setSelectedMail] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dossiers = [...new Set(allMails.map(mail => mail.dossier))].sort();
  const typesFichier = ['Contrat', 'PGC', 'PreDIUO', 'DIUO', 'VIC', 'RJ', 'Facture', 'Mail'];

  // Fonction pour raccourcir les types de fichiers trop longs
  const getTruncatedType = (typefichier, maxLength = 20) => {
    if (typefichier.length <= maxLength) {
      return typefichier;
    }

    if (typefichier.toLowerCase().includes('contrat') && typefichier.toLowerCase().includes('avenant')) {
      return 'CONTRAT AVENANT';
    }

    if (typefichier.toLowerCase().includes('facture') && typefichier.toLowerCase().includes('recapitulative')) {
      return 'FACTURE RECAP';
    }

    if (typefichier.toLowerCase().includes('facture') && typefichier.toLowerCase().includes('conception')) {
      return 'FACTURE CONCEPT';
    }

    if (typefichier.toLowerCase().includes('facture') && typefichier.toLowerCase().includes('realisation')) {
      return 'FACTURE REAL';
    }

    if (typefichier.toLowerCase().includes('registre') && typefichier.toLowerCase().includes('journal')) {
      return 'RJ';
    }

    if (typefichier.toLowerCase().includes('pré') && typefichier.toLowerCase().includes('diuo')) {
      return 'PreDIUO';
    }

    return typefichier.substring(0, maxLength - 3) + '...';
  };

  // Fonction pour générer la classe CSS du badge de type
  const getTypeClass = (typefichier) => {
    const type = typefichier.toLowerCase();

    // Types avec préfixes - vérifiés en premier
    if (type.startsWith('facture')) {
      return 'facture';
    }
    else if (type.startsWith('contrat')) {
      return 'contrat';
    }

    // Cas spéciaux avec correspondances textuelles
    else if (type.includes('registre') && type.includes('journal')) {
      return 'rj';
    }
    else if (type.includes('pré') && type.includes('diuo')) {
      return 'prediuo';
    }

    // Types exacts (vérification exacte avec toLowerCase)
    else if (type === 'pgc') {
      return 'pgc';
    }
    else if (type === 'prediuo') {
      return 'prediuo';
    }
    else if (type === 'diuo') {
      return 'diuo';
    }
    else if (type === 'vic') {
      return 'vic';
    }
    else if (type === 'rj') {
      return 'rj';
    }
    else if (type === 'mail') {
      return 'mail';
    }

    // Pour tous les autres types non reconnus → style mail (gris)
    else {
      return 'mail';
    }
  };

  const formatDateAsLocal = (dateString) => {
    const localDateString = dateString.replace('T', ' ').replace('.000Z', '');
    const [datePart, timePart] = localDateString.split(' ');
    const [year, month, day] = datePart.split('-');
    const [hours, minutes, seconds] = timePart.split(':');
    const localDate = new Date(year, month - 1, day, hours, minutes, seconds);

    return localDate.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatMailData = (mailData) => {
    return mailData.map(({ id, ndossier, type, indication, recipient, sent_at }) => ({
      id,
      dossier: ndossier.toString(),
      typefichier: type,
      indication,
      destinataire: parseRecipient(recipient),
      dateEnvoi: formatDateAsLocal(sent_at)
    }));
  };

  const parseRecipient = (recipient) => {
    try {
      return recipient.startsWith('[') && recipient.endsWith(']')
        ? JSON.parse(recipient)
        : recipient;
    } catch {
      return recipient;
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchAllMails = async () => {
      try {
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        };

        const res = await fetch("/api/mail/getAllMails", requestOptions);
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        const newData = formatMailData(data);

        setAllMails(newData);
      } catch (error) {
        console.error('Erreur lors de la récupération des lots:', error);
      }
    };

    fetchAllMails();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let filtered = allMails;

    if (selectedDossier !== 'all') {
      filtered = filtered.filter(mail => mail.dossier === selectedDossier);
    }

    if (selectedType !== 'all') {
      if (selectedType === 'Facture') {
        filtered = filtered.filter(mail =>
          mail.typefichier.toLowerCase().startsWith('facture')
        );
      } else if (selectedType === 'Contrat') {
        filtered = filtered.filter(mail =>
          mail.typefichier.toLowerCase().startsWith('contrat')
        );
      } else if (selectedType === 'Mail') {
        filtered = filtered.filter(mail => {
          const type = mail.typefichier.toLowerCase();
          const typeExact = mail.typefichier;
          const typesExactsDB = ['PGC', 'PreDIUO', 'DIUO', 'VIC', 'RJ', 'Registre Journal', 'Pré DIUO'];

          return typeExact === 'Mail' ||
            (!type.startsWith('facture') &&
              !type.startsWith('contrat') &&
              !typesExactsDB.includes(typeExact));
        });
      } else {
        if (selectedType === 'RJ') {
          filtered = filtered.filter(mail =>
            mail.typefichier === 'RJ' || mail.typefichier === 'Registre Journal'
          );
        } else if (selectedType === 'PreDIUO') {
          filtered = filtered.filter(mail =>
            mail.typefichier === 'PreDIUO' || mail.typefichier === 'Pré DIUO'
          );
        } else {
          filtered = filtered.filter(mail =>
            mail.typefichier === selectedType
          );
        }
      }
    }

    setFilteredMails(filtered);
    setCurrentPage(1);
  }, [selectedDossier, selectedType, allMails]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedMails(filteredMails.slice(startIndex, endIndex));
  }, [filteredMails, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredMails.length / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredMails.length);

  const formatDestinataires = (destinataire) => {
    if (typeof destinataire === 'string') {
      return destinataire;
    }

    if (Array.isArray(destinataire)) {
      if (destinataire.length <= 2) {
        return destinataire.join(', ');
      } else {
        return `${destinataire.slice(0, 2).join(', ')}, +${destinataire.length - 2} autre${destinataire.length - 2 === 1 ? '' : 's'}`;
      }
    }

    return destinataire;
  };

  const resetFilters = () => {
    setSelectedDossier('all');
    setSelectedType('all');
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const changeItemsPerPage = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const openMailDetails = (mail) => {
    setSelectedMail(mail);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMail(null);
  };

  // Fonction pour copier toutes les adresses email du destinataire
  const copyAllEmails = async (destinataire) => {
    try {
      let emailsToCopy = '';

      if (typeof destinataire === 'string') {
        emailsToCopy = destinataire;
      } else if (Array.isArray(destinataire)) {
        emailsToCopy = destinataire.join(', ');
      }

      await navigator.clipboard.writeText(emailsToCopy);

    } catch (error) {
      console.error('Erreur lors de la copie:', error);

      const textArea = document.createElement('textarea');
      textArea.value = Array.isArray(destinataire) ? destinataire.join(', ') : destinataire;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // Fonction pour copier une seule adresse email
  const copySingleEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);

    } catch (error) {
      console.error('Erreur lors de la copie:', error);

      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className='tracking-mail'>
      <Header />
      {isLoading && <LoadingOverlay />}
      <div className="mail-tracking">
        <div className="mail-tracking__header">
          <h2 className="mail-tracking__title mail-tracking__title--gray">Suivi des mails</h2>
          <div className="mail-tracking__stats">
            {filteredMails.length} mail{filteredMails.length > 1 ? 's' : ''} trouvé{filteredMails.length > 1 ? 's' : ''}
          </div>
        </div>

        <div className="mail-tracking__filters">
          <div className="filter-group">
            <label htmlFor="dossier-filter" className="filter-label">
              Dossier
            </label>
            <select
              id="dossier-filter"
              className="filter-select"
              value={selectedDossier}
              onChange={(e) => setSelectedDossier(e.target.value)}
            >
              <option value="all">Tous les dossiers</option>
              {dossiers.map(dossier => (
                <option key={dossier} value={dossier}>
                  {dossier}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="type-filter" className="filter-label">
              Type de fichier
            </label>
            <select
              id="type-filter"
              className="filter-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">Tous les types</option>
              {typesFichier.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <button
            className="reset-button"
            onClick={resetFilters}
            disabled={selectedDossier === 'all' && selectedType === 'all'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Réinitialiser
          </button>
        </div>

        <div className="mail-tracking__table-container">
          <table className="mail-table">
            <thead>
              <tr>
                <th>Dossier</th>
                <th>Type</th>
                <th>Indication</th>
                <th>Destinataire</th>
                <th>Date et heure d'envoi</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMails.length > 0 ? (
                paginatedMails.map(mail => (
                  <tr
                    key={mail.id}
                    className="mail-row"
                    onClick={() => openMailDetails(mail)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span className="dossier-badge">
                        {mail.dossier}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`type-badge type-badge--${getTypeClass(mail.typefichier)}`}
                        title={mail.typefichier}
                      >
                        {getTruncatedType(mail.typefichier)}
                      </span>
                    </td>
                    <td className="indication">
                      {mail.indication}
                    </td>
                    <td className="destinataire">
                      {formatDestinataires(mail.destinataire)}
                    </td>
                    <td className="date-envoi">
                      {mail.dateEnvoi}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-results">
                    <div className="no-results__content">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <p>Aucun mail trouvé avec ces critères</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredMails.length > 0 && (
          <div className="pagination">
            <div className="pagination__info">
              Affichage de {startItem} à {endItem} sur {filteredMails.length} résultats
            </div>

            <div className="pagination__controls">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Afficher:</span>
                <select
                  className="pagination__select"
                  value={itemsPerPage}
                  onChange={(e) => changeItemsPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <button
                className="pagination__button"
                onClick={goToPrevious}
                disabled={currentPage === 1}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Précédent
              </button>

              <div className="pagination__page-numbers">
                {getPageNumbers().map((pageNumber, index) => (
                  pageNumber === '...' ? (
                    <span key={index} className="pagination__ellipsis">...</span>
                  ) : (
                    <button
                      key={index}
                      className={`pagination__page-button ${currentPage === pageNumber ? 'pagination__page-button--active' : ''}`}
                      onClick={() => goToPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  )
                ))}
              </div>

              <button
                className="pagination__button"
                onClick={goToNext}
                disabled={currentPage === totalPages}
              >
                Suivant
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {isModalOpen && selectedMail && (
          <MailDetailsModal
            mail={selectedMail}
            onClose={closeModal}
            formatDestinataires={formatDestinataires}
            getTypeClass={getTypeClass}
            copyAllEmails={copyAllEmails}
            copySingleEmail={copySingleEmail}
          />
        )}
      </div>
    </div>
  );
};

const MailDetailsModal = ({
  mail,
  onClose,
  formatDestinataires,
  getTypeClass,
  copyAllEmails,
  copySingleEmail
}) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Détails du Mail</h3>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="mail-details">
            <div className="detail-row">
              <span className="detail-label">Dossier:</span>
              <span className="dossier-badge">{mail.dossier}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Type de fichier:</span>
              <span
                className={`type-badge type-badge--${getTypeClass(mail.typefichier)}`}
                title={mail.typefichier}
              >
                {mail.typefichier}
              </span>
            </div>

            {mail.typefichier === "VIC" &&
              <>
                <div className="detail-row">
                  <span className="detail-label">Lot:</span>
                  <span className="detail-value">{mail.indication.split(" - ")[0]}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Entreprise:</span>
                  <span className="detail-value">{mail.indication.split(" - ")[1]}</span>
                </div>
              </>}

            {["Registre Journal", "PGC", "Pré DIUO", "DIUO"].includes(mail.typefichier) && (
              <div className="detail-row">
                <span className="detail-label">Indice:</span>
                <span className="detail-value">{mail.indication}</span>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">Destinataire{Array.isArray(mail.destinataire) ? 's' : ''}:</span>
              <div className="destinataires-list">
                {Array.isArray(mail.destinataire)
                  ? mail.destinataire.map((dest, index) => (
                    <span
                      key={index}
                      className="destinataire-item destinataire-item--clickable"
                      onClick={(e) => {
                        e.stopPropagation();
                        copySingleEmail(dest);
                      }}
                      title={`Cliquer pour copier: ${dest}`}
                    >
                      {dest}
                    </span>
                  ))
                  : <span
                    className="destinataire-item destinataire-item--clickable"
                    onClick={(e) => {
                      e.stopPropagation();
                      copySingleEmail(mail.destinataire);
                    }}
                    title={`Cliquer pour copier: ${mail.destinataire}`}
                  >
                    {formatDestinataires(mail.destinataire)}
                  </span>
                }
              </div>
            </div>

            <div className="detail-row">
              <span className="detail-label">Date d'envoi:</span>
              <span className="detail-value date-envoi">{mail.dateEnvoi}</span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-copy"
            onClick={() => copyAllEmails(mail.destinataire)}
            title="Copier toutes les adresses email"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Copier les emails
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MailTracking;